import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Globe, BookOpen, Award, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const HighlandGlobalPage = () => {
  const collaborations = [
    {
      title: 'University of Edinburgh',
      description: 'Joint workshops on climate-altered health systems in Highland Asia',
      icon: Globe
    },
    {
      title: 'Danish Institute of International Studies',
      description: 'Funding partnership for MyCClimate project',
      icon: Award
    },
    {
      title: 'Norwegian Directorate of Higher Education',
      description: 'Support for Ekologos environmental research initiative',
      icon: BookOpen
    }
  ];

  const visitingScholars = [
    {
      name: 'Dr. Bérénice Guyot-Réchard',
      affiliation: 'Kings College London',
      focus: 'International and South Asian History'
    },
    {
      name: 'Dr. Edward Moon-Little',
      affiliation: 'Social Anthropologist',
      focus: 'Indigenous Astrology and Cultural Studies'
    },
    {
      name: 'Dr. Michael T. Heneise',
      affiliation: 'University of Edinburgh',
      focus: 'Medical Anthropology and Plural Health Systems'
    },
    {
      name: 'Prof. Luiz Marques',
      affiliation: 'Ekologos Project',
      focus: 'Environmental Tipping Points and Climate Science'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=600&fit=crop)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-blue-700/60"></div>
        </div>
        <div className="relative h-full flex items-center">
          <div className="container mx-auto px-4">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Highland Global</h1>
            <p className="text-xl text-white/90 max-w-2xl">
              Connecting the Highlands to the World
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              International Research Networks and Partnerships
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Highland Global represents our commitment to fostering international collaboration, bringing
              together scholars, institutions, and communities from around the world to address shared challenges
              and create sustainable solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Global Collaborations */}
      <section id="collaborations" className="py-16 bg-gray-50 scroll-mt-24">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-2">Global Collaborations</h2>
            <div className="h-1 w-24 bg-green-600"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {collaborations.map((collab, index) => {
              const Icon = collab.icon;
              return (
                <Card key={index} className="hover:shadow-xl transition-all duration-300">
                  <CardHeader>
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="p-3 bg-green-100 rounded-lg">
                        <Icon className="w-6 h-6 text-green-600" />
                      </div>
                    </div>
                    <CardTitle className="text-xl">{collab.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{collab.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Visiting Scholars */}
      <section id="scholars" className="py-16 bg-white scroll-mt-24">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-2">Visiting Scholars & Fellows</h2>
            <div className="h-1 w-24 bg-green-600"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {visitingScholars.map((scholar, index) => (
              <Card key={index} className="hover:shadow-lg transition-all duration-300 border-t-4 border-t-green-600">
                <CardHeader>
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-10 h-10 text-green-600" />
                  </div>
                  <CardTitle className="text-lg text-center">{scholar.name}</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-sm text-green-600 font-semibold mb-2">{scholar.affiliation}</p>
                  <p className="text-sm text-gray-600">{scholar.focus}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* International Conferences */}
      <section id="conferences" className="py-16 bg-gray-50 scroll-mt-24">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-2">International Conferences & Workshops</h2>
            <div className="h-1 w-24 bg-green-600"></div>
          </div>
          <div className="max-w-4xl mx-auto">
            <Card className="shadow-lg">
              <CardContent className="p-8">
                <p className="text-gray-600 leading-relaxed mb-6">
                  The Highland Institute regularly hosts and participates in international conferences, workshops,
                  and symposia that bring together researchers, practitioners, and community leaders to share
                  knowledge and forge new partnerships.
                </p>
                <div className="space-y-4">
                  <div className="border-l-4 border-green-600 pl-4">
                    <h3 className="font-bold text-gray-900 mb-2">Recent Events</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li>• International Workshop on Entangled Medical Futures (University of Edinburgh)</li>
                      <li>• 16th NEN Biodiversity Festival</li>
                      <li>• Annual Ursula Graham Bower Lectures</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Partner With Us */}
      <section id="partner" className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white scroll-mt-24">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Partner With Us</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            We welcome collaborations with institutions, researchers, and organizations worldwide.
            Together, we can create meaningful impact.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-3 text-lg">
              Get in Touch
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HighlandGlobalPage;